/**
 * Dental CSSD AI Service Client
 * Cloudflare Tunnel & AI Server integration with seamless local fallback.
 * 
 * Supports:
 * - Server health checking with latency measurement
 * - Receipt OCR with Qwen2.5-VL and automatic Tesseract.js fallback
 * - Material classification for visual inventory identification
 */

import { performReceiptOCR, parseReceiptText } from './receiptOcrHelper';

/**
 * Returns the current AI configuration from environment variables.
 * @returns {{ AI_SERVER_URL: string, AI_API_KEY: string, AI_MODE: 'auto'|'server'|'local' }}
 */
export const getAiConfig = () => {
  return {
    AI_SERVER_URL: (process.env.REACT_APP_AI_SERVER_URL || '').replace(/\/+$/, ''),
    AI_API_KEY: process.env.REACT_APP_AI_API_KEY || '',
    AI_MODE: process.env.REACT_APP_AI_MODE || 'auto'
  };
};

/**
 * Checks the connectivity and latency to the AI server.
 * Pings /health or /api/v1/health with an AbortController timeout.
 * 
 * @param {number} timeoutMs Max wait time in milliseconds (default 3000ms)
 * @returns {Promise<{ isOnline: boolean, latencyMs: number, error: string|null, url: string }>}
 */
export const checkAiServerHealth = async (timeoutMs = 3000) => {
  const { AI_SERVER_URL, AI_API_KEY } = getAiConfig();

  if (!AI_SERVER_URL) {
    return {
      isOnline: false,
      latencyMs: 0,
      error: 'AI_SERVER_URL is not configured',
      url: ''
    };
  }

  const startTime = Date.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const headers = {};
    if (AI_API_KEY) {
      headers['X-Api-Key'] = AI_API_KEY;
    }

    let response;
    try {
      response = await fetch(`${AI_SERVER_URL}/health`, {
        method: 'GET',
        headers,
        signal: controller.signal
      });

      // If /health returns 404, try alternative /api/v1/health
      if (response.status === 404) {
        response = await fetch(`${AI_SERVER_URL}/api/v1/health`, {
          method: 'GET',
          headers,
          signal: controller.signal
        });
      }
    } finally {
      clearTimeout(timeoutId);
    }

    const latencyMs = Date.now() - startTime;

    if (response && response.ok) {
      return {
        isOnline: true,
        latencyMs,
        error: null,
        url: AI_SERVER_URL
      };
    }

    return {
      isOnline: false,
      latencyMs,
      error: `Server responded with status ${response ? response.status : 'unknown'}`,
      url: AI_SERVER_URL
    };
  } catch (err) {
    clearTimeout(timeoutId);
    const latencyMs = Date.now() - startTime;
    const isTimeout = err.name === 'AbortError';

    return {
      isOnline: false,
      latencyMs,
      error: isTimeout ? `Connection timed out after ${timeoutMs}ms` : (err?.message || 'Connection failed'),
      url: AI_SERVER_URL
    };
  }
};

/**
 * Utility to convert various image input formats into a Blob suitable for FormData upload.
 * Supports File, Blob, base64 data URL, remote URL string, Canvas, and Image elements.
 * 
 * @param {Blob|File|string|HTMLCanvasElement|HTMLImageElement} imageSource
 * @returns {Promise<Blob>}
 */
export const convertImageSourceToBlob = async (imageSource) => {
  if (!imageSource) {
    throw new Error('Image source is required');
  }

  // Already a Blob or File
  if (imageSource instanceof Blob || (typeof File !== 'undefined' && imageSource instanceof File)) {
    return imageSource;
  }

  // String: Base64 Data URL or fetchable URL
  if (typeof imageSource === 'string') {
    if (imageSource.startsWith('data:')) {
      const parts = imageSource.split(',');
      if (parts.length < 2) {
        throw new Error('Invalid Data URL format');
      }
      const match = parts[0].match(/:(.*?);/);
      const mime = match ? match[1] : 'image/jpeg';
      const bstr = atob(parts[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    }

    // Remote / local URL
    const res = await fetch(imageSource);
    if (!res.ok) {
      throw new Error(`Failed to fetch image source: ${res.statusText}`);
    }
    return await res.blob();
  }

  // HTMLCanvasElement
  if (imageSource && typeof imageSource.toBlob === 'function') {
    return new Promise((resolve, reject) => {
      imageSource.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to convert canvas to blob'));
      }, 'image/jpeg', 0.95);
    });
  }

  // HTMLImageElement
  if (typeof window !== 'undefined' && typeof document !== 'undefined' && imageSource && imageSource.tagName === 'IMG') {
    const canvas = document.createElement('canvas');
    canvas.width = imageSource.naturalWidth || imageSource.width || 800;
    canvas.height = imageSource.naturalHeight || imageSource.height || 600;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D context unavailable');
    }
    ctx.drawImage(imageSource, 0, 0);
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to convert image element to blob'));
      }, 'image/jpeg', 0.95);
    });
  }

  throw new Error('Unsupported image source type');
};

/**
 * Requests Receipt OCR extraction from the AI server or falls back to local Tesseract.js.
 * 
 * @param {Blob|File|string|HTMLCanvasElement|HTMLImageElement} imageSource
 * @param {object} options
 * @param {Function} [options.onProgress] Callback receiving ({ status: string, progress: number })
 * @param {AbortSignal} [options.signal] Optional abort signal
 * @param {'auto'|'server'|'local'} [options.forceEngine] Force engine bypass
 * @returns {Promise<{
 *   receiptNumber: string,
 *   poNumber: string,
 *   supplierName: string,
 *   receivedDate: string,
 *   totalAmount: string,
 *   detectedItems: Array<{ item_name: string, quantity: number, unit: string, unit_price: number, lot_number?: string, exp_date?: string }>,
 *   rawText: string,
 *   confidence: number,
 *   engine: 'server'|'local'|'local_fallback'
 * }>}
 */
export const requestReceiptOcrAI = async (imageSource, options = {}) => {
  const { onProgress = () => {}, signal, forceEngine } = options;
  const config = getAiConfig();
  const effectiveEngine = forceEngine || config.AI_MODE;

  // Local-only mode or server not configured
  if (effectiveEngine === 'local' || !config.AI_SERVER_URL) {
    onProgress({ status: 'ประมวลผลด้วยเครื่องลูกข่าย (Local Engine)...', progress: 10 });
    const localResult = await performReceiptOCR(imageSource, onProgress);
    const parsed = parseReceiptText(localResult?.text || '');
    return {
      receiptNumber: parsed.receiptNumber || '',
      poNumber: parsed.poNumber || '',
      supplierName: parsed.supplierName || '',
      receivedDate: parsed.receivedDate || '',
      totalAmount: parsed.totalAmount || '',
      detectedItems: parsed.detectedItems || [],
      rawText: localResult?.text || '',
      confidence: localResult?.confidence || 0,
      engine: 'local'
    };
  }

  // Server or Auto mode
  try {
    onProgress({ status: 'กำลังเตรียมรูปภาพและส่งไปยัง AI Server...', progress: 15 });
    const blob = await convertImageSourceToBlob(imageSource);
    const formData = new FormData();
    formData.append('file', blob, 'receipt.jpg');

    onProgress({ status: 'Local AI Server (Mac M4 / Synology NAS) กำลังวิเคราะห์ข้อมูลใบเสร็จ...', progress: 40 });

    const headers = {};
    if (config.AI_API_KEY) {
      headers['X-Api-Key'] = config.AI_API_KEY;
    }

    const response = await fetch(`${config.AI_SERVER_URL}/api/v1/ocr/receipt`, {
      method: 'POST',
      headers,
      body: formData,
      signal
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(`AI Server error (${response.status}): ${errText || response.statusText}`);
    }

    onProgress({ status: 'รับข้อมูลผลลัพธ์จาก AI Server สำเร็จ', progress: 90 });
    const data = await response.json();

    onProgress({ status: 'เสร็จสิ้นกระบวนการวิเคราะห์', progress: 100 });

    // Normalize returned items array
    const rawItems = Array.isArray(data.detectedItems)
      ? data.detectedItems
      : Array.isArray(data.items)
      ? data.items
      : [];

    const normalizedItems = rawItems.map(item => ({
      item_name: item.item_name || item.name || '',
      quantity: Number(item.quantity) || 1,
      unit: item.unit || 'กล่อง',
      unit_price: Number(item.unit_price || item.price) || 0,
      lot_number: item.lot_number || item.lot || '',
      exp_date: item.exp_date || item.expiry || '',
      mfg_date: item.mfg_date || ''
    }));

    return {
      receiptNumber: data.receiptNumber || data.receipt_number || '',
      poNumber: data.poNumber || data.po_number || '',
      supplierName: data.supplierName || data.supplier_name || '',
      receivedDate: data.receivedDate || data.received_date || '',
      totalAmount: data.totalAmount ? String(data.totalAmount) : (data.total_amount ? String(data.total_amount) : ''),
      detectedItems: normalizedItems,
      rawText: data.rawText || data.raw_text || data.text || '',
      confidence: typeof data.confidence === 'number' ? data.confidence : 95,
      engine: 'server'
    };
  } catch (serverErr) {
    // If auto mode, fallback to local OCR
    if (effectiveEngine === 'auto') {
      console.warn('AI Server OCR failed, falling back to local OCR:', serverErr);
      onProgress({ status: 'AI Server ขัดข้อง กำลังเปลี่ยนไปใช้ Local Engine ในเบราว์เซอร์...', progress: 20 });
      try {
        const localResult = await performReceiptOCR(imageSource, onProgress);
        const parsed = parseReceiptText(localResult?.text || '');
        return {
          receiptNumber: parsed.receiptNumber || '',
          poNumber: parsed.poNumber || '',
          supplierName: parsed.supplierName || '',
          receivedDate: parsed.receivedDate || '',
          totalAmount: parsed.totalAmount || '',
          detectedItems: parsed.detectedItems || [],
          rawText: localResult?.text || '',
          confidence: localResult?.confidence || 0,
          engine: 'local_fallback'
        };
      } catch (localErr) {
        console.error('Both AI Server and Local OCR fallback failed:', localErr);
        throw new Error(`การอ่านข้อมูลล้มเหลว (Server: ${serverErr.message}, Local: ${localErr.message})`);
      }
    }

    // If forceEngine is 'server', propagate the server error
    throw serverErr;
  }
};

/**
 * Requests Material Classification from AI Server (Visual ID).
 * 
 * @param {Blob|File|string|HTMLCanvasElement|HTMLImageElement} imageSource
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{
 *   className: string,
 *   thaiName: string,
 *   confidence: number,
 *   suggestedCategory: string,
 *   matchedMasterItemId: string|number|null
 * }>}
 */
export const requestMaterialClassificationAI = async (imageSource, options = {}) => {
  const { signal } = options;
  const config = getAiConfig();

  if (!config.AI_SERVER_URL) {
    throw new Error('AI Server URL is not configured');
  }

  const blob = await convertImageSourceToBlob(imageSource);
  const formData = new FormData();
  formData.append('file', blob, 'material.jpg');

  const headers = {};
  if (config.AI_API_KEY) {
    headers['X-Api-Key'] = config.AI_API_KEY;
  }

  const response = await fetch(`${config.AI_SERVER_URL}/api/v1/classify/material`, {
    method: 'POST',
    headers,
    body: formData,
    signal
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new Error(`AI Server classification error (${response.status}): ${errText || response.statusText}`);
  }

  const data = await response.json();

  return {
    className: data.className || data.class_name || '',
    thaiName: data.thaiName || data.thai_name || '',
    confidence: typeof data.confidence === 'number' ? data.confidence : 0,
    suggestedCategory: data.suggestedCategory || data.suggested_category || '',
    matchedMasterItemId: data.matchedMasterItemId ?? data.matched_master_item_id ?? null
  };
};

/**
 * Alias for requestMaterialClassificationAI for backward/convenience compatibility.
 */
export const classifyMaterialAI = requestMaterialClassificationAI;

/**
 * Requests Leave Form OCR extraction from the AI server (Synology NAS OCR service).
 * Extracts structured leave information: employee name, department, leave type,
 * dates, duration, reason, and confidence level.
 * 
 * @param {Blob|File|string|HTMLCanvasElement|HTMLImageElement} fileSource Document image or PDF
 * @param {object} [options]
 * @param {Function} [options.onProgress] Callback receiving ({ status: string, progress: number })
 * @param {AbortSignal} [options.signal] Optional abort signal
 * @param {string} [options.forceEngine] Optional engine override parameter
 * @returns {Promise<{
 *   employeeName: string,
 *   department: string,
 *   leaveType: string,
 *   startDate: string,
 *   endDate: string,
 *   startDateRaw: string,
 *   endDateRaw: string,
 *   totalDays: number,
 *   reason: string,
 *   confidence: string|number,
 *   rawText: string,
 *   engine: 'synology-nas'
 * }>}
 */
export const requestLeaveOcrAI = async (fileSource, options = {}) => {
  const { onProgress = () => {}, signal } = options;
  const config = getAiConfig();

  if (!config.AI_SERVER_URL) {
    throw new Error('AI Server URL is not configured. Please configure REACT_APP_AI_SERVER_URL in your environment.');
  }

  onProgress({ status: 'กำลังเตรียมเอกสารใบลา...', progress: 15 });
  const blob = await convertImageSourceToBlob(fileSource);

  const formData = new FormData();
  const filename = (typeof File !== 'undefined' && fileSource instanceof File && fileSource.name)
    ? fileSource.name
    : (blob.type === 'application/pdf' ? 'leave_document.pdf' : 'leave_document.jpg');
  formData.append('file', blob, filename);

  const headers = {};
  if (config.AI_API_KEY) {
    headers['X-Api-Key'] = config.AI_API_KEY;
  }

  onProgress({ status: 'กำลังส่งเอกสารไปยัง AI Server (Synology NAS)...', progress: 40 });

  let response;
  try {
    response = await fetch(`${config.AI_SERVER_URL}/api/v1/ocr/leave`, {
      method: 'POST',
      headers,
      body: formData,
      signal
    });

    // Fallback to /api/ocr/leave if /api/v1/ocr/leave returns 404
    if (response.status === 404) {
      response = await fetch(`${config.AI_SERVER_URL}/api/ocr/leave`, {
        method: 'POST',
        headers,
        body: formData,
        signal
      });
    }
  } catch (err) {
    throw err;
  }

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    let errMsg = errText || response.statusText;
    try {
      const parsed = JSON.parse(errText);
      if (parsed && (parsed.detail || parsed.message)) {
        errMsg = parsed.detail || parsed.message;
      }
    } catch {
      // Retain errText as error message
    }
    throw new Error(`AI Server error (${response.status}): ${errMsg}`);
  }

  onProgress({ status: 'รับข้อมูลผลลัพธ์จาก AI Server สำเร็จ', progress: 90 });
  const data = await response.json();

  onProgress({ status: 'เสร็จสิ้นกระบวนการวิเคราะห์', progress: 100 });

  return {
    employeeName: data.employee_name || data.employeeName || '',
    department: data.department || '',
    leaveType: data.leave_type || data.leaveType || '',
    startDate: data.start_date_iso || data.start_date || '',
    endDate: data.end_date_iso || data.end_date || '',
    startDateRaw: data.start_date || '',
    endDateRaw: data.end_date || '',
    totalDays: typeof data.total_days === 'number' ? data.total_days : (parseFloat(data.total_days) || 1),
    reason: data.reason || '',
    confidence: data.confidence || 'medium',
    rawText: data.raw_text || data.raw_text_preview || '',
    engine: 'synology-nas'
  };
};

/**
 * Normalizes date strings into ISO format (YYYY-MM-DD), converting Thai Buddhist Era (พ.ศ.) if detected.
 * @param {string} dateStr 
 * @returns {string}
 */
export const normalizeDateToIso = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const trimmed = dateStr.trim();
  if (!trimmed) return '';

  // Case 1: YYYY-MM-DD or YYYY/MM/DD
  const isoMatch = trimmed.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (isoMatch) {
    let year = parseInt(isoMatch[1], 10);
    const month = String(parseInt(isoMatch[2], 10)).padStart(2, '0');
    const day = String(parseInt(isoMatch[3], 10)).padStart(2, '0');
    if (year > 2400) {
      year -= 543;
    }
    return `${year}-${month}-${day}`;
  }

  // Case 2: DD/MM/YYYY or DD-MM-YYYY
  const dmyMatch = trimmed.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/);
  if (dmyMatch) {
    const day = String(parseInt(dmyMatch[1], 10)).padStart(2, '0');
    const month = String(parseInt(dmyMatch[2], 10)).padStart(2, '0');
    let year = parseInt(dmyMatch[3], 10);
    if (year < 100) {
      // 2-digit Buddhist year: e.g. 67 -> 2567 -> 2024
      year = year > 50 ? (2500 + year) - 543 : 2000 + year;
    } else if (year > 2400) {
      year -= 543;
    }
    return `${year}-${month}-${day}`;
  }

  return trimmed;
};

/**
 * Parses and validates JSON generated by Local AI Server (Mac M4 / Synology NAS)
 * or imported from local order files.
 * Normalizes fields to match Dental CSSD schema.
 * 
 * @param {string|object} jsonInput JSON string or object
 * @returns {{
 *   receiptNumber: string,
 *   poNumber: string,
 *   supplierName: string,
 *   receivedDate: string,
 *   totalAmount: string,
 *   detectedItems: Array<{
 *     item_name: string,
 *     quantity: number,
 *     unit: string,
 *     unit_price: number,
 *     lot_number: string,
 *     exp_date: string,
 *     mfg_date: string
 *   }>,
 *   rawText: string,
 *   confidence: number,
 *   engine: string
 * }}
 */
export const parseLocalOrderJson = (jsonInput) => {
  if (!jsonInput) {
    throw new Error('ไม่พบข้อมูล JSON หรือข้อมูลว่างเปล่า');
  }

  let data = jsonInput;
  if (typeof jsonInput === 'string') {
    try {
      data = JSON.parse(jsonInput);
    } catch (err) {
      throw new Error(`รูปแบบไฟล์ JSON ไม่ถูกต้อง: ${err.message}`);
    }
  }

  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    throw new Error('โครงสร้างข้อมูล JSON ไม่ถูกต้อง (ต้องเป็น Object)');
  }

  const receiptNumber = String(
    data.receiptNumber || data.receipt_number || data.invoice_number || data.invoiceNumber || data.bill_no || data.billNo || ''
  ).trim();

  const poNumber = String(
    data.poNumber || data.po_number || data.purchase_order || data.purchaseOrder || data.po || ''
  ).trim();

  const supplierName = String(
    data.supplierName || data.supplier_name || data.supplier || data.vendor || data.company || ''
  ).trim();

  const rawDate = data.receivedDate || data.received_date || data.date || data.invoice_date || '';
  const receivedDate = normalizeDateToIso(String(rawDate));

  let rawTotal = data.totalAmount ?? data.total_amount ?? data.total ?? data.grandTotal ?? data.grand_total ?? '';
  let totalAmount = '';
  if (typeof rawTotal === 'number') {
    totalAmount = String(rawTotal);
  } else if (typeof rawTotal === 'string') {
    totalAmount = rawTotal.replace(/[^0-9.-]/g, '').trim();
  }

  const rawItems = Array.isArray(data.detectedItems)
    ? data.detectedItems
    : Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.detected_items)
    ? data.detected_items
    : [];

  const detectedItems = rawItems.map((item) => {
    if (!item || typeof item !== 'object') {
      return {
        item_name: '',
        quantity: 1,
        unit: 'กล่อง',
        unit_price: 0,
        lot_number: '',
        exp_date: '',
        mfg_date: ''
      };
    }

    const qtyNum = Number(item.quantity ?? item.qty ?? 1);
    const priceNum = Number(item.unit_price ?? item.price ?? item.unitPrice ?? 0);

    return {
      item_name: String(item.item_name || item.name || item.description || item.item || '').trim(),
      quantity: isNaN(qtyNum) || qtyNum < 0 ? 1 : qtyNum,
      unit: String(item.unit || 'กล่อง').trim() || 'กล่อง',
      unit_price: isNaN(priceNum) || priceNum < 0 ? 0 : priceNum,
      lot_number: String(item.lot_number || item.lot || item.batch_number || item.batch || '').trim(),
      exp_date: normalizeDateToIso(String(item.exp_date || item.expiry || item.expDate || '')),
      mfg_date: normalizeDateToIso(String(item.mfg_date || item.mfg || item.mfgDate || ''))
    };
  });

  const rawText = data.rawText || data.raw_text || data.text || (
    `[Local AI Server] Receipt: ${receiptNumber || '-'}, Supplier: ${supplierName || '-'}, Total: ${totalAmount || '-'}`
  );

  const confidence = typeof data.confidence === 'number'
    ? data.confidence
    : (parseFloat(data.confidence) || 98.0);

  const engine = data.engine || 'local_server';

  return {
    receiptNumber,
    poNumber,
    supplierName,
    receivedDate,
    totalAmount,
    detectedItems,
    rawText,
    confidence,
    engine
  };
};

// Backwards compatibility alias
export const parseGeminiSparkJson = parseLocalOrderJson;


